"use client";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";

interface ImageTextSectionProps {
  title: string;
  content: string | React.ReactNode;
  imageSrc: string;
  imageAlt: string;
  imagePosition?: 'left' | 'right';
  buttonText?: string;
  buttonHref?: string;
  buttonVariant?: 'default' | 'outline';
  imageOverlay?: boolean;
  imageHeight?: string;
  className?: string;
  children?: React.ReactNode;
}

export default function ImageTextSection({
  title,
  content,
  imageSrc,
  imageAlt,
  imagePosition = 'right',
  buttonText,
  buttonHref,
  buttonVariant = 'outline',
  imageOverlay = false,
  imageHeight = 'h-[400px]',
  className = '',
  children
}: ImageTextSectionProps) {
  const locale = useLocale();
  const translate = (text: string) => t(locale, text);
  const isExternalButtonHref = buttonHref ? /^https?:\/\//.test(buttonHref) : false;
  const localizedButtonHref = buttonHref && !isExternalButtonHref
    ? getLocalizedPathname(buttonHref, locale)
    : buttonHref;
  const translatedTitle = translate(title);
  const translatedContent = typeof content === "string"
    ? translate(content)
    : content;
  const translatedButtonText = buttonText ? translate(buttonText) : "";

  const textContent = (
    <div>
      <h2 className="text-3xl font-bold text-neutral-emphasis mb-6">{translatedTitle}</h2>
      {typeof translatedContent === 'string' ? (
        <div className="text-neutral-muted mb-4 text-justify space-y-4">
          {translatedContent.split('\n\n').map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
      ) : (
        <div className="mb-4">{translatedContent}</div>
      )}
      {children}
      {buttonText && localizedButtonHref && (
        <Link href={localizedButtonHref}>
          <Button variant={buttonVariant} className="mt-2">
            {translatedButtonText} <i className="fas fa-arrow-right ml-2"></i>
          </Button>
        </Link>
      )}
    </div>
  );

  const imageContent = (
    <div className={cn("relative rounded-lg overflow-hidden shadow-xl", imageHeight)}>
      {imageOverlay && (
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-black/40 z-10"></div>
      )}
      <img 
        src={imageSrc} 
        alt={imageAlt} 
        className="w-full h-full object-cover"
      />
    </div>
  );

  return (
    <div className={cn("grid grid-cols-1 lg:grid-cols-2 gap-12 items-center", className)}>
      {imagePosition === 'left' ? (
        <>
          <div className="order-2 lg:order-1">{imageContent}</div>
          <div className="order-1 lg:order-2">{textContent}</div>
        </>
      ) : (
        <>
          <div>{textContent}</div>
          <div>{imageContent}</div>
        </>
      )}
    </div>
  );
}
